import { TableSearch, TableText } from "@/components/table-tools";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ADMIN_REFRESH_MS } from "@/lib/admin";
import {
  createConnectQueryKey,
  useMutation,
  useQuery,
} from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";

import {
  adminListDevices,
  adminListEdges,
  adminListWorkers,
  adminSetDevicePrivateLaneCount,
  adminSetExitBinding,

} from "@/gen/nodebase/v1/admin-AdminService_connectquery";
import { WorkerTierDialog } from "@/components/worker-tier-dialog";
import { rebuildWorker } from "@/gen/nodebase/v1/worker-WorkerService_connectquery";
import type { AdminEdge, AdminWorker } from "@/gen/nodebase/v1/admin_pb";
import { DeviceState } from "@/gen/nodebase/v1/device_pb";
import { EdgeRole } from "@/gen/nodebase/v1/edge_pb";
import { WorkerState } from "@/gen/nodebase/v1/worker_pb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyNote, ErrorNote, Loading, PageHeader } from "@/components/page";
import { FleetScope } from "@/components/scope";
import {
  ago,
  enumLabel,
  fmtBitrate,
  fmtBytes,
  stateTone,
  transportLabel,
  tunnelLive,
} from "@/lib/format";

const FRESH_SECONDS = 120;
const PRESSURE_RATIO = 0.85;

// One page for both fleets: workers and devices are the two things an account
// can own, and the operator question ("what exists on this control plane, and
// whose is it") is the same for both.
export function AdminFleet() {
  const edges = useQuery(adminListEdges, {}, { retry: false, refetchInterval: ADMIN_REFRESH_MS });
  const workers = useQuery(adminListWorkers, {}, { retry: false, refetchInterval: ADMIN_REFRESH_MS });
  const devices = useQuery(adminListDevices, {}, { retry: false, refetchInterval: ADMIN_REFRESH_MS });
  const workerRows = [...(workers.data?.workers ?? [])].sort(
    (a, b) => workerAttention(b).length - workerAttention(a).length,
  );
  const deviceRows = devices.data?.devices ?? [];
  const [workerSearch, setWorkerSearch] = useState("");
  const [deviceSearch, setDeviceSearch] = useState("");
  const visibleWorkers = workerRows.filter((row) => [row.worker?.id, row.ownerEmail, row.worker?.region, row.worker?.overlayIp, row.tier, enumLabel(WorkerState, row.worker?.state)].join(" ").toLowerCase().includes(workerSearch.trim().toLowerCase()));
  const visibleDevices = deviceRows.filter((row) => [row.device?.id, row.device?.name, row.ownerEmail, enumLabel(DeviceState, row.device?.state)].join(" ").toLowerCase().includes(deviceSearch.trim().toLowerCase()));
  const exitPool = workers.data?.exitIpPool ?? [];
  // Who already sits on each pool IP, so the picker can say so: sharing an
  // assigned IP is legitimate, but it should be a choice the operator makes
  // knowingly, not a surprise.
  const exitUsage = new Map<string, string[]>();
  for (const row of workerRows) {
    if (row.exitIp) {
      exitUsage.set(row.exitIp, [
        ...(exitUsage.get(row.exitIp) ?? []),
        row.ownerEmail || row.worker?.workspaceId || "?",
      ]);
    }
  }

  return (
    <>
      <PageHeader title="Fleet" scope={<FleetScope noun="Workers and devices" />} />
      <ErrorNote error={edges.error ?? workers.error ?? devices.error} />

      {!workers.isPending && !workers.isError && (
        <WorkerSummary rows={workerRows} linkMbps={workers.data?.linkMbps ?? 0} />
      )}

      <EdgeFleet rows={(edges.data?.edges ?? []).filter((row) => !ingressOnly(row))} pending={edges.isPending} />
      <EdgeFleet rows={(edges.data?.edges ?? []).filter(ingressOnly)} pending={edges.isPending} ingress />

      <h2 className="mb-3 text-base font-semibold tracking-tight">Workers</h2>
      <TableSearch label="Search Workers by owner, ID, region or tier" value={workerSearch} onChange={setWorkerSearch} count={visibleWorkers.length} total={workerRows.length} />
      {workers.isPending ? (
        <Loading />
      ) : workers.isError ? null : workerRows.length === 0 ? (
        <EmptyNote>No workers exist on this control plane.</EmptyNote>
      ) : (
        <Table aria-label="Workers">
          <TableHeader>
            <TableRow>
              <TableHead>Worker</TableHead>
              <TableHead>Health</TableHead>
              <TableHead>Network</TableHead>
              <TableHead title="Current CPU share and memory/disk used, from the Worker's authenticated heartbeat">
                Resources
              </TableHead>
              <TableHead title="Near-real-time WG rate plus cumulative rx / tx since the worker's interface came up">
                Activity
              </TableHead>
              <TableHead title="Which public IP this workspace's egress leaves through">
                Controls
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleWorkers.length === 0 && <TableRow><TableCell colSpan={6}>No Workers match your search.</TableCell></TableRow>}
            {visibleWorkers.map((row) => {
              const w = row.worker;
              const state = enumLabel(WorkerState, w?.state);
              const attention = workerAttention(row);
              return (
                <TableRow key={w?.id} data-testid="fleet-row">
                  {/* Owner rides under the id: both answer "whose is this",
                      and the table's width budget is spent on live state. */}
                  <TableCell>
                    <code className="text-xs">{w?.id}</code>
                    <div className="text-xs text-muted-foreground">
                      <TableText value={row.ownerEmail || "—"} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={stateTone(state)}>{state}</Badge>
                    <div className="mt-1 text-xs text-muted-foreground">
                      heartbeat {ago(w?.lastHeartbeatUnix)}
                    </div>
                    {attention.length > 0 && (
                      <div className="mt-1 text-xs text-destructive" data-testid="worker-attention">
                        {attention.join(" · ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>{w?.region || "—"}</div>
                    <code className="text-xs">{w?.overlayIp || "—"}</code>
                    <div className="text-xs text-muted-foreground">
                      agent {w?.agentVersion || "—"}
                    </div>
                    {/* Container rebuild targets do not apply to native VMs,
                        including responses from older Control versions. */}
                    <div className="text-xs text-muted-foreground" data-testid="worker-runtime">
                      runtime {row.runtime || "—"}
                      {row.runtime !== "native-vm" && row.desiredRuntime && row.desiredRuntime !== row.runtime && (
                        <span className="text-destructive"> → {row.desiredRuntime} on rebuild</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {row.resourceReportedUnix > 0n ? (
                      <>
                        <div className="tabular-nums">
                          CPU {row.cpuPct.toFixed(1)}%
                        </div>
                        <div className="text-xs text-muted-foreground tabular-nums">
                          memory {fmtBytes(row.memUsedMb * 1024n * 1024n)} / {w?.spec?.memMb ?? 0} MB
                        </div>
                        <div className="text-xs text-muted-foreground tabular-nums">
                          disk {fmtBytes(row.diskUsedMb * 1024n * 1024n)} / {w?.spec?.diskGb ?? 0} GB
                        </div>
                        <div className="text-xs text-muted-foreground">
                          reported {ago(row.resourceReportedUnix)}
                        </div>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">awaiting agent report</span>
                    )}
                  </TableCell>
                  <TableCell title={row.trafficReportedUnix ? `reported ${ago(row.trafficReportedUnix)}` : undefined}>
                    <div className="tabular-nums">
                      {fmtBitrate(row.wgRxRateBytesSec)} ↓ / {fmtBitrate(row.wgTxRateBytesSec)} ↑
                    </div>
                    <div className="text-xs text-muted-foreground tabular-nums">
                      total {row.trafficReportedUnix
                        ? `${fmtBytes(row.wgRxBytes)} ↓ / ${fmtBytes(row.wgTxBytes)} ↑`
                        : "—"}
                    </div>
                  </TableCell>
                  <TableCell>
                    {w?.id && w?.workspaceId && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        Exit IP:
                        <ExitBindingDialog
                          workerId={w.id}
                          runtime={row.runtime}
                          workspaceId={w.workspaceId}
                          exitIp={row.exitIp}
                          pendingRebuild={row.exitPendingRebuild}
                          pool={exitPool}
                          usage={exitUsage}
                        />
                      </div>
                    )}
                    {w?.id && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        Tier: <WorkerTierDialog row={row} tiers={workers.data?.tiers ?? []} />
                      </div>
                    )}
                    {w?.id && row.runtime === "native-vm" && <Link className="mt-2 inline-flex rounded-md border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring" to={`/admin/services?worker=${encodeURIComponent(w.id)}`}>VM &amp; firewall management</Link>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <h2 className="mb-3 mt-8 text-base font-semibold tracking-tight">
        Devices
      </h2>
      <TableSearch label="Search devices by owner, name, ID or state" value={deviceSearch} onChange={setDeviceSearch} count={visibleDevices.length} total={deviceRows.length} />
      {devices.isPending ? (
        <Loading />
      ) : devices.isError ? null : deviceRows.length === 0 ? (
        <EmptyNote>No devices exist on this control plane.</EmptyNote>
      ) : (
        <Table aria-label="Fleet devices">
          <TableHeader>
            <TableRow>
              <TableHead>Device</TableHead>
              <TableHead>Health</TableHead>
              <TableHead title="Overlay address and the worker endpoint this device's tunnel uses; hover for the device source seen by the worker">
                Network
              </TableHead>
              <TableHead title="This device's split of the worker's cumulative WG counters: rx = device → worker, tx = worker → device">
                Traffic
              </TableHead>
              <TableHead>Private lanes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleDevices.length === 0 && <TableRow><TableCell colSpan={5}>No devices match your search.</TableCell></TableRow>}
            {visibleDevices.map((row) => {
              const d = row.device;
              const state = enumLabel(DeviceState, d?.state);
              const handshake =
                (d?.wgHandshakeUnix ?? 0n) > 0n
                  ? d?.wgHandshakeUnix
                  : d?.linkHandshakeUnix;
              return (
                <TableRow key={d?.id} data-testid="device-fleet-row">
                  <TableCell>
                    <div className="font-medium"><TableText value={d?.name || "—"} /></div>
                    <code className="text-xs">{d?.id}</code>
                    <div className="text-xs text-muted-foreground">
                      <TableText value={row.ownerEmail || "—"} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={stateTone(state)}>{state}</Badge>
                    <div className="text-xs text-muted-foreground">
                      {d?.platform || "—"} · {d?.clientVersion || "—"}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {(handshake ?? 0n) > 0n ? (
                        <>
                          handshake {ago(handshake)}{" "}
                          {tunnelLive(handshake) && (
                            <Badge variant="success">live</Badge>
                          )}
                        </>
                      ) : (
                        "no handshake"
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <code>{d?.overlayIp || "—"}</code>
                    </div>
                    {d && d.linkReportedUnix > 0n ? (
                      <span
                        title={
                          d.wgEndpoint
                            ? `device source ${d.wgEndpoint}`
                            : undefined
                        }
                      >
                        <code className="text-xs">
                          {d.linkEndpointHost || "?"}:{d.linkEndpointPort || "?"}
                        </code>{" "}
                        {transportLabel(d.linkTransport)}
                      </span>
                    ) : d && d.wgReportedUnix > 0n ? (
                      // Tunnel observed worker-side, endpoint never
                      // self-reported: an old client, and the blank says so.
                      <span
                        className="text-xs text-muted-foreground"
                        title="The endpoint is self-reported by the device's client; this one predates 0.2.36 (or has not polled since updating)"
                      >
                        needs 0.2.36
                      </span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell
                    title={
                      d && d.wgReportedUnix > 0n
                        ? `reported ${ago(d.wgReportedUnix)}`
                        : undefined
                    }
                  >
                    {d && d.wgReportedUnix > 0n
                      ? `${fmtBytes(d.wgRxBytes)} ↑ / ${fmtBytes(d.wgTxBytes)} ↓`
                      : "—"}
                    {row.privateLanes.map((lane) => (
                      <div key={lane.lane?.laneId} className="mt-1 text-xs text-muted-foreground tabular-nums">
                        lane {lane.lane?.laneId}: {lane.wgReportedUnix > 0n
                          ? `${fmtBytes(lane.wgRxBytes)} ↑ / ${fmtBytes(lane.wgTxBytes)} ↓`
                          : "awaiting Worker"}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell>
                    <PrivateLaneDialog
                      deviceId={d?.id ?? ""}
                      desiredCount={d?.desiredPrivateLaneCount ?? 1}
                      activeCount={1 + row.privateLanes.length}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );
}

function PrivateLaneDialog({
  deviceId,
  desiredCount,
  activeCount,
}: {
  deviceId: string;
  desiredCount: number;
  activeCount: number;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const next = desiredCount === 2 ? 1 : 2;
  const update = useMutation(adminSetDevicePrivateLaneCount, {
    onSuccess: () => {
      setOpen(false);
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({ schema: adminListDevices, cardinality: undefined }),
      });
    },
  });
  return (
    <Dialog open={open} onOpenChange={(value) => { update.reset(); setOpen(value); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={!deviceId} data-testid="private-lane-count">
          {activeCount}/{desiredCount}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{next === 2 ? "Enable a second private lane" : "Return to one private lane"}</DialogTitle>
          <DialogDescription>
            This is an exact-Device canary for <code className="text-xs">{deviceId}</code>.
            {next === 2
              ? " The client allocates a distinct WireGuard key and address on its next poll."
              : " New flows return to lane 0 and Control revokes the auxiliary Worker peer."}
          </DialogDescription>
        </DialogHeader>
        <ErrorNote error={update.error} />
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={() => update.mutate({ deviceId, desiredCount: next })} disabled={update.isPending}>
            {update.isPending ? "Applying…" : `Set ${next} lane${next === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fresh(unix: bigint | number | undefined): boolean {
  const value = Number(unix ?? 0);
  return value > 0 && Date.now() / 1000 - value < FRESH_SECONDS;
}

function workerAttention(row: AdminWorker): string[] {
  const worker = row.worker;
  const reasons: string[] = [];
  const state = enumLabel(WorkerState, worker?.state);
  if (state !== "ready") reasons.push(state === "unknown" ? "unknown state" : state);
  if (!fresh(worker?.lastHeartbeatUnix)) reasons.push("stale heartbeat");
  if (row.resourceReportedUnix === 0n) {
    reasons.push("no resource report");
    return reasons;
  }
  if (!fresh(row.resourceReportedUnix)) reasons.push("stale resources");
  if (row.cpuPct >= PRESSURE_RATIO * 100) reasons.push("CPU pressure");
  if (worker?.spec?.memMb && Number(row.memUsedMb) / worker.spec.memMb >= PRESSURE_RATIO) {
    reasons.push("memory pressure");
  }
  if (worker?.spec?.diskGb && Number(row.diskUsedMb) / (worker.spec.diskGb * 1024) >= PRESSURE_RATIO) {
    reasons.push("disk pressure");
  }
  return reasons;
}

function WorkerSummary({ rows, linkMbps }: { rows: AdminWorker[]; linkMbps: number }) {
  const healthy = rows.filter((row) => workerAttention(row).length === 0).length;
  const attention = rows.length - healthy;
  const freshResources = rows.filter((row) => fresh(row.resourceReportedUnix)).length;
  const liveBytesPerSecond = rows.reduce(
    (sum, row) =>
      sum +
      Math.max(0, Number(row.wgRxRateBytesSec)) +
      Math.max(0, Number(row.wgTxRateBytesSec)),
    0,
  );
  const utilization = linkMbps > 0
    ? `${((liveBytesPerSecond * 8 * 100) / (linkMbps * 1_000_000)).toFixed(1)}% of ${linkMbps} Mbps`
    : "link ceiling not configured";

  return (
    <section className="mb-8" aria-labelledby="worker-operations-heading">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="worker-operations-heading" className="text-base font-semibold tracking-tight">
          Worker operations
        </h2>
        <span className="text-xs text-muted-foreground">Workers needing attention are listed first.</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <FleetSummary label="Healthy workers" value={`${healthy}/${rows.length}`} />
        <FleetSummary label="Need attention" value={String(attention)} attention={attention > 0} />
        <FleetSummary label="Fresh resource reports" value={`${freshResources}/${rows.length}`} />
        <FleetSummary label={utilization} value={fmtBitrate(liveBytesPerSecond)} />
      </div>
    </section>
  );
}

function FleetSummary({
  label,
  value,
  attention = false,
}: {
  label: string;
  value: string;
  attention?: boolean;
}) {
  return (
    <Card data-testid="fleet-summary">
      <CardContent className="p-4">
        <div className={`text-2xl font-semibold tabular-nums ${attention ? "text-destructive" : ""}`}>
          {value}
        </div>
        <div className="text-sm text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

function ingressOnly(row: AdminEdge): boolean {
  return row.edge?.roles.length === 1 && row.edge.roles[0] === EdgeRole.INGRESS;
}

function retiredEdge(row: AdminEdge): boolean {
  return row.edge?.status === "revoked" && row.assignments.length === 0;
}

function EdgeFleet({
  rows: allRows,
  pending,
  ingress = false,
}: {
  rows: AdminEdge[];
  pending: boolean;
  ingress?: boolean;
}) {
  const [showRetired, setShowRetired] = useState(false);
  const retired = allRows.filter(retiredEdge).length;
  const rows = allRows.filter((row) => showRetired || !retiredEdge(row));
  const ordered = [...rows].sort(
    (a, b) => edgeAttention(b).length - edgeAttention(a).length,
  );
  const attention = rows.filter((row) => edgeAttention(row).length > 0).length;
  return (
    <>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold tracking-tight">{ingress ? "Public Web gateways" : "Edge gateways"}</h2>
        {!pending && rows.length > 0 && (
          <span className={`text-xs ${attention > 0 ? "text-destructive" : "text-muted-foreground"}`}>
            {attention > 0 ? `${attention} need attention` : "All current snapshots are within operating margins"}
          </span>
        )}
      </div>
      {retired > 0 && <Button size="sm" variant="outline" className="mb-3" aria-pressed={showRetired} onClick={() => setShowRetired(!showRetired)}>{showRetired ? "Hide retired gateways" : `Show ${retired} retired gateways`}</Button>}
      {ingress && <p className="mb-3 text-xs text-muted-foreground">Website ingress heartbeats and route configuration. HTTP and TLS health checks are in Services.</p>}
      {pending ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyNote>No current {ingress ? "Public Web" : "Edge"} gateways.</EmptyNote>
      ) : (
        <Table aria-label={ingress ? "Public Web gateways" : "Transport gateways"}>
          <TableHeader>
            <TableRow>
              <TableHead>Edge</TableHead>
              <TableHead>Capabilities</TableHead>
              <TableHead>Endpoints</TableHead>
              <TableHead>Capacity &amp; config</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ordered.map((row) => {
              const edge = row.edge;
              const runtime = edge?.runtimeStats;
              const reasons = edgeAttention(row);
              const relayAssignments = row.assignments.filter(
                (a) => a.role === EdgeRole.RELAY,
              ).length;
              const exitAssignments = row.assignments.filter(
                (a) => a.role === EdgeRole.EXIT,
              ).length;
              const converged =
                edge?.desiredConfigVersion === edge?.observedConfigVersion;
              const endpoints = [...(edge?.endpoints ?? [])].sort(
                (a, b) =>
                  a.priority - b.priority ||
                  `${a.host}:${a.port}`.localeCompare(`${b.host}:${b.port}`),
              );
              return (
                <TableRow key={edge?.id} data-testid="edge-fleet-row">
                  <TableCell>
                    <code className="text-xs">{edge?.id}</code>
                    <div className="text-xs text-muted-foreground">
                      {edge?.region || "—"} · {edge?.agentVersion || "unregistered"}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <Badge variant={retiredEdge(row) ? "secondary" : stateTone(edge?.status || "unknown")}>
                        {edge?.status || "unknown"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        heartbeat {ago(edge?.lastHeartbeatUnix)}
                      </span>
                    </div>
                    {reasons.length > 0 && (
                      <div className="mt-1 text-xs text-destructive" data-testid="edge-attention">
                        {reasons.join(" · ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      {edge?.roles.map((role) => enumLabel(EdgeRole, role)).join(", ") || "—"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {ingressOnly(row) ? "Website ingress" : `${relayAssignments} relay · ${exitAssignments} exit`}
                    </div>
                  </TableCell>
                  <TableCell>
                    {endpoints.length ? (
                      <div className="space-y-1">
                        {endpoints.map((endpoint, index) => (
                          <div
                            key={`${endpoint.host}:${endpoint.port}/${endpoint.transport}`}
                            data-testid="edge-endpoint"
                            className="flex flex-wrap items-center gap-1"
                          >
                            <code className="text-xs">
                              {endpoint.host}:{endpoint.port}
                            </code>
                            <Badge variant={index === 0 ? "secondary" : "outline"}>
                              {index === 0 ? "primary" : "fallback"}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {transportLabel(endpoint.transport)} · priority{" "}
                              {endpoint.priority}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {runtime ? `${runtime.exitActiveSessions}/${runtime.exitCapacity} Exit` : edge ? `${edge.capacityUsed}/${edge.capacity}` : "—"}
                    <div className="text-xs text-muted-foreground">
                      {edge?.activeSessions ?? 0} total · {runtime ? `${runtime.pendingHandshakes}/${runtime.pendingCapacity} pending` : ingressOnly(row) ? "active website connections" : retiredEdge(row) ? "retired" : "awaiting runtime report"}
                    </div>
                    {runtime && (
                      <>
                        <div className="mt-1 text-xs text-muted-foreground">
                          memory {runtime.memoryLimitBytes > 0n ? `${fmtBytes(runtime.memoryUsedBytes)} / ${fmtBytes(runtime.memoryLimitBytes)}` : fmtBytes(runtime.memoryUsedBytes)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          FD {runtime.fdUsed.toString()}/{runtime.fdLimit > 0n ? runtime.fdLimit.toString() : "?"} · conntrack {runtime.conntrackUsed.toString()}/{runtime.conntrackLimit > 0n ? runtime.conntrackLimit.toString() : "?"}
                        </div>
                        <div className="text-xs text-muted-foreground" title={edgeRejectDetail(runtime)}>
                          rejects {edgeRejects(runtime).toString()} · auth avg {runtime.handshakeLatencySamples > 0n ? `${Number(runtime.handshakeLatencyMsTotal / runtime.handshakeLatencySamples)} ms` : "—"}
                        </div>
                      </>
                    )}
                    <div className="mt-1 text-xs text-muted-foreground">
                      config {edge ? `${edge.observedConfigVersion}/${edge.desiredConfigVersion}` : "—"}{" "}
                    </div>
                    {edge && (
                      <Badge variant={converged ? "success" : "warning"}>
                        {converged ? "applied" : "pending"}
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <div className="mb-8" />
    </>
  );
}

function edgeRejects(runtime: NonNullable<AdminEdge["edge"]>["runtimeStats"]): bigint {
  if (!runtime) return 0n;
  return runtime.pendingRejectedTotal + runtime.sourceRejectedTotal +
    runtime.handshakeRejectedTotal + runtime.capacityRejectedTotal +
    runtime.exitCapacityRejectedTotal + runtime.exitWorkspaceRejectedTotal +
    runtime.exitRateRejectedTotal + runtime.exitQuotaRejectedTotal;
}

function edgeRejectDetail(runtime: NonNullable<AdminEdge["edge"]>["runtimeStats"]): string {
  if (!runtime) return "Awaiting runtime report";
  return [
    `pending ${runtime.pendingRejectedTotal}`,
    `source ${runtime.sourceRejectedTotal}`,
    `handshake ${runtime.handshakeRejectedTotal}`,
    `total capacity ${runtime.capacityRejectedTotal}`,
    `Exit capacity ${runtime.exitCapacityRejectedTotal}`,
    `workspace ${runtime.exitWorkspaceRejectedTotal}`,
    `rate ${runtime.exitRateRejectedTotal}`,
    `quota ${runtime.exitQuotaRejectedTotal}`,
  ].join(" · ") + " · cumulative since this Edge process started";
}

function edgeAttention(row: AdminEdge): string[] {
  const edge = row.edge;
  const reasons: string[] = [];
  if (!edge) return ["missing Edge state"];
  if (retiredEdge(row)) return [];
  if (edge.status !== "ready") reasons.push(edge.status || "unknown state");
  if (!fresh(edge.lastHeartbeatUnix)) reasons.push("stale heartbeat");
  if (edge.desiredConfigVersion !== edge.observedConfigVersion) reasons.push("configuration pending");
  if (ingressOnly(row)) {
    if (edge.capacity > 0 && edge.capacityUsed / edge.capacity >= PRESSURE_RATIO) reasons.push("ingress capacity pressure");
    return reasons;
  }
  const runtime = edge.runtimeStats;
  if (!runtime || !fresh(runtime.reportedUnix)) {
    reasons.push(runtime ? "stale runtime report" : "no runtime report");
    return reasons;
  }
  if (runtime.exitCapacity > 0 && runtime.exitActiveSessions / runtime.exitCapacity >= 0.7) {
    reasons.push("Exit capacity pressure");
  }
  if (runtime.pendingCapacity > 0 && runtime.pendingHandshakes / runtime.pendingCapacity >= 0.7) {
    reasons.push("handshake pressure");
  }
  if (runtime.memoryLimitBytes > 0n && Number(runtime.memoryUsedBytes) / Number(runtime.memoryLimitBytes) >= PRESSURE_RATIO) {
    reasons.push("memory pressure");
  }
  if (runtime.fdLimit > 0n && Number(runtime.fdUsed) / Number(runtime.fdLimit) >= 0.7) {
    reasons.push("FD pressure");
  }
  if (runtime.conntrackLimit > 0n && Number(runtime.conntrackUsed) / Number(runtime.conntrackLimit) >= 0.7) {
    reasons.push("conntrack pressure");
  }
  return reasons;
}

// Exit assignments update native VM source rules live. Retained container
// workers may need a rebuild to acquire their reserved source address.
function ExitBindingDialog({
  workerId,
  runtime,
  workspaceId,
  exitIp,
  pendingRebuild,
  pool,
  usage,
}: {
  workerId: string;
  workspaceId: string;
  exitIp: string;
  runtime: string;
  pendingRebuild: boolean;
  pool: string[];
  usage: Map<string, string[]>;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [ip, setIp] = useState("");
  const [note, setNote] = useState("");

  const refetchFleet = () =>
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: adminListWorkers,
        cardinality: undefined,
      }),
    });

  const isNative = runtime === "native-vm";
  const bind = useMutation(adminSetExitBinding, {
    onSuccess: () => refetchFleet(),
  });
  const rebuild = useMutation(rebuildWorker, {
    onSuccess: () => {
      setOpen(false);
      refetchFleet();
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setIp(exitIp);
          setNote("");
          bind.reset();
          rebuild.reset();
        }
        setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="exit-binding"
          className="min-h-8 px-2 py-1 font-medium text-foreground"
        >
          {exitIp ? (
            <span>
              <code className="text-xs">{exitIp}</code>
              {!isNative && pendingRebuild ? " ⚠ rebuild" : ""}
            </span>
          ) : (
            "default"
          )}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assigned exit IP</DialogTitle>
          <DialogDescription>
            Routes workspace <code className="text-xs">{workspaceId}</code>
            &apos;s outbound traffic through an assigned exit IP. Several
            workspaces may share one.{" "}
            {isNative
              ? "Native VM changes apply to new connections without rebuilding."
              : "A container rebuild may be required to apply the assignment."}{" "}
            Clearing the assignment returns new connections to the default exit.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            bind.mutate({ workspaceId, exitIp: ip.trim(), note });
          }}
        >
          <ErrorNote error={bind.error ?? rebuild.error} />
          {bind.data && (
            <p className="text-sm text-muted-foreground" data-testid="bind-result">
              {bind.data.containerIp
                ? !isNative && bind.data.rebuildRequired
                  ? "Assigned. Rebuild the worker for it to take effect."
                  : "Assigned; new connections use this exit."
                : "Assignment cleared; new connections use the default exit."}
            </p>
          )}
          <div className="grid gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={`exit-${workerId}`}>Exit IP</Label>
              <Select
                id={`exit-${workerId}`}
                value={ip}
                onChange={(e) => setIp(e.target.value)}
              >
                <option value="">default (no assigned exit)</option>
                {pool.map((p) => {
                  const holders = usage.get(p) ?? [];
                  const label =
                    p === exitIp
                      ? `${p} (current)`
                      : holders.length > 0
                        ? `${p} — in use by ${holders.join(", ")}`
                        : p;
                  return (
                    <option key={p} value={p}>
                      {label}
                    </option>
                  );
                })}
              </Select>
              {pool.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No exit IP pool configured.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`exit-note-${workerId}`}>Note</Label>
              <Input
                id={`exit-note-${workerId}`}
                value={note}
                placeholder="why this workspace gets a dedicated exit"
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Close
              </Button>
            </DialogClose>
            {!isNative && (pendingRebuild || bind.data?.rebuildRequired) && (
              <Button
                type="button"
                variant="outline"
                disabled={rebuild.isPending}
                data-testid="exit-rebuild"
                onClick={() => rebuild.mutate({ workerId })}
              >
                Rebuild worker now
              </Button>
            )}
            <Button type="submit" disabled={bind.isPending}>
              Apply
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
